import React, { Component } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { AlertIcon, Button, colors } from '@commonsku/styles';

import { addPartial, removePartial } from '../actions/order';
import { formatMoney, parseMysqlDate, getAbsoluteUrl } from '../utils';
import { FLAG_COMMONSKU_PAYMENTS, FLAG_COMMONSKU_PAYMENTS_QBO_REFUNDS_UI, CREDIT_CARD_PROVIDER_DB_VALUE, CREDIT_CARD_STATUS } from './admin-new/CreditCardIntegration';
import { RefundFailureTooltip } from './RefundFailureTooltip';

const SelectAllHeaderCell = styled.th`
    vertical-align: middle;
`;

const NoMarginCheckbox = styled.input`
    /* &&& needed: ties Foundation's [type=checkbox] margin rule on specificity */
    &&& {
        margin-bottom: 0;
    }
`;

const RetryAlertCell = styled.td`
    padding: 0;
    border: none;
`;

const RetryAlertBar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    background: ${colors.errors.lightest};
    color: ${colors.errors.dark};
    padding: 10px 16px;
`;

const RetryAlertMessage = styled.span`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const RetryAlertErrorMessage = styled.div`
    color: ${colors.errors.main};
    padding: 0 16px 8px;
`;

const CheckboxCell = styled.td`
    vertical-align: middle;
`;

const CheckboxWithIcon = styled.span`
    display: inline-flex;
    align-items: center;
`;

const FailureIconWrapper = styled.span`
    margin-left: 8px;
    display: inline-flex;
`;

class PartialList extends Component {

    constructor(props) {
        super(props);

        this.state = {
            selected: props.selected
        };
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        this.setState({ selected: nextProps.selected });
    }

    enabledCommonskuPayments = () => this.props.identity?.user_flags?.includes(FLAG_COMMONSKU_PAYMENTS) || false;

    enabledRefundsUI = () => this.props.identity?.user_flags?.includes(FLAG_COMMONSKU_PAYMENTS_QBO_REFUNDS_UI) || false;

    renderPaymentHeaders() {
        if (!this.enabledCommonskuPayments()) {
            return null;
        }
        return (
            <>
                <th style={{ textAlign: 'left' }}>Payment status</th>
                <th style={{ textAlign: 'left' }}>Payment provider</th>
                <th style={{ textAlign: 'left' }}>Payment amount</th>
            </>
        );
    }

    renderPaymentCells(o) {
        if (!this.enabledCommonskuPayments()) {
            return null;
        }
        return (
            <>
                <td>{CREDIT_CARD_STATUS[o.payment_status] ?? CREDIT_CARD_STATUS.NO_DATA}</td>
                <td>{CREDIT_CARD_PROVIDER_DB_VALUE[o.payment_provider ?? '']?.label ?? CREDIT_CARD_STATUS.NONE}</td>
                <td>{o.payment_amount === null || o.payment_amount === undefined ? CREDIT_CARD_STATUS.NO_DATA : `$${formatMoney(o.payment_amount)}`}</td>
            </>
        );
    }

    render() {
        const {
            partials, handleClickPartial, handleSelectAllPartials, partials_loaded, emptyMessage,
            onRetryFailedRefunds, retryingRefunds, retryRefundsError,
        } = this.props;
        const { selected } = this.state;
        const colSpan = this.enabledCommonskuPayments() ? 9 : 6;
        const failedPartials = partials.filter(o => o.refund_status === 'REFUND_FAILED');

        if (!partials_loaded) return <div style={{ textAlign: 'center'}}><img alt="loading" style={{ margin: '1rem' }} src="/images/gears.gif" /></div>;

        return (
            <table className='partials-table'>
                <thead className="order-summary">
                    <tr>
                        <SelectAllHeaderCell><NoMarginCheckbox type="checkbox" checked={selected.length === partials.length} onClick={e => {e.stopPropagation(); handleSelectAllPartials(selected.length === partials.length);}} /></SelectAllHeaderCell>
                        <th style={{ textAlign: 'left' }}>Created</th>
                        <th style={{ textAlign: 'left' }}>Reference</th>
                        <th style={{ textAlign: 'left' }}>Customer Name</th>
                        <th style={{ textAlign: 'left' }}>Email</th>
                        {this.renderPaymentHeaders()}
                        <th style={{ textAlign: 'left' }}>Value</th>
                    </tr>
                </thead>
                <tbody>
                    {onRetryFailedRefunds && failedPartials.length > 0 &&
                        <tr>
                            <RetryAlertCell colSpan={colSpan}>
                                <RetryAlertBar>
                                    <RetryAlertMessage>
                                        <AlertIcon size="small" />
                                        Some checkouts couldn&apos;t be refunded. Check the affected checkouts and retry.
                                    </RetryAlertMessage>
                                    <Button
                                        size="small"
                                        variant="secondary"
                                        disabled={retryingRefunds}
                                        onClick={e => { e.preventDefault(); e.stopPropagation(); onRetryFailedRefunds(failedPartials); }}
                                    >
                                        {retryingRefunds ? 'Retrying...' : 'Retry all'}
                                    </Button>
                                </RetryAlertBar>
                                {retryRefundsError &&
                                    <RetryAlertErrorMessage>{retryRefundsError}</RetryAlertErrorMessage>
                                }
                            </RetryAlertCell>
                        </tr>
                    }
                    {partials.length ?
                        partials.map(o =>
                            <tr key={o.order_id} onClick={e => {e.stopPropagation(); handleClickPartial(o.order_id);}}>
                                <CheckboxCell>
                                    <CheckboxWithIcon>
                                        <NoMarginCheckbox type="checkbox" checked={selected.includes(o.order_id)} />
                                        {'REFUND_FAILED' === o.refund_status && this.enabledRefundsUI() &&
                                            <FailureIconWrapper onClick={e => e.stopPropagation()}>
                                                <RefundFailureTooltip
                                                    tooltipId={`refund-failed-${o.order_id}`}
                                                    refundErrorType={o.refund_error_type}
                                                />
                                            </FailureIconWrapper>
                                        }
                                    </CheckboxWithIcon>
                                </CheckboxCell>
                                <td>{parseMysqlDate(o.date_created)}</td>
                                <td>
                                    <a href={getAbsoluteUrl(`public_order.php?order_id=${o.order_id}`)} target="_blank" rel="noopener noreferrer">
                                        {o.customer_po}
                                    </a>
                                </td>
                                <td>{o.checked_out_by}</td>
                                <td>{o.checked_out_email}</td>
                                {this.renderPaymentCells(o)}
                                <td>${formatMoney(o.total_subtotal)}</td>
                            </tr>)
                    :
                        <tr><td colSpan={colSpan} style={{ textAlign: 'center' }}>{emptyMessage || 'No partial orders available'}</td></tr>
                    }
                </tbody>
            </table>
        );
    }
}

const mapStateToProps = (state, ownProps) => ({
    identity: state.identity ?? false,
});

const mapDispatchToProps = (dispatch, ownProps) => {
    return {
        onAddPartial: (order_id, parent_order_id) => {
            dispatch(addPartial(order_id, parent_order_id));
        },
        onRemovePartial: (order_id, parent_order_id) => {
            dispatch(removePartial(order_id, parent_order_id));
        }
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(PartialList);
